import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Sparkles, X, Send, Paperclip, FileCode } from 'lucide-react';
import { apiClient, type WorkflowDefinition } from '../../api/client';
import { isMobileViewport } from '../../hooks/useMobile';

interface WorkflowNLInputProps {
  onGenerated: (definition: WorkflowDefinition, triggerConfig: any, name: string, description: string, prompt: string) => void;
  initialPrompt?: string;
  /** Simulated builder mode: generation treats every integration as connected. */
  simulated?: boolean;
}

// joinNaturally renders ["a", "b", "c"] as "a, b and c" for user-facing lists.
function joinNaturally(items: string[]): string {
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** In-panel notice shown under the prompt. Warnings (capability gaps,
 * unconnected integrations) render amber; failures render destructive red.
 * Neither auto-dismisses — cleared on the next generate attempt. */
interface PanelNotice {
  message: string;
  severity: 'error' | 'warning';
}

// inferScriptLanguage maps a filename extension to the Execute Script block's
// language field. Defaults to python.
function inferScriptLanguage(filename: string): 'python' | 'shell' {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.sh') || lower.endsWith('.bash')) return 'shell';
  return 'python';
}

interface AttachedScript {
  name: string;
  content: string;
  language: 'python' | 'shell';
}

// injectScriptIntoDefinition deterministically populates the uploaded script
// into a generated Execute Script (kestrel-execute-script) node so the script
// body is preserved verbatim. If the agent created multiple Execute Script
// nodes, the first one with an empty/placeholder script is filled; if none
// qualify, the first Execute Script node is used.
function injectScriptIntoDefinition(def: WorkflowDefinition, script: AttachedScript): WorkflowDefinition {
  const nodes = def.nodes || [];
  const execNodes = nodes.filter(n => n?.data?.action === 'kestrel-execute-script');
  if (execNodes.length === 0) return def;

  const isEmptyScript = (s: any) => typeof s !== 'string' || s.trim() === '';
  const target = execNodes.find(n => isEmptyScript(n?.data?.config?.script)) || execNodes[0];

  return {
    ...def,
    nodes: nodes.map(n => {
      if (n.id !== target.id) return n;
      return {
        ...n,
        data: {
          ...n.data,
          config: {
            ...(n.data?.config || {}),
            script: script.content,
            language: script.language,
          },
        },
      };
    }),
  };
}

export function WorkflowNLInput({ onGenerated, initialPrompt, simulated }: WorkflowNLInputProps) {
  const [prompt, setPrompt] = useState(initialPrompt || '');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<PanelNotice | null>(null);
  // Open by default on desktop — the panel is the primary way into the builder,
  // so it shouldn't need a click to discover. Collapsed on mobile, where it
  // would cover most of the screen and hide the canvas. Read once at mount: a
  // reactive breakpoint would collapse the panel mid-typing on rotation.
  const [expanded, setExpanded] = useState(!isMobileViewport());
  const [attachedScript, setAttachedScript] = useState<AttachedScript | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialPrompt && !prompt) {
      setPrompt(initialPrompt);
    }
  }, [initialPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScriptFile = (file: File) => {
    setNotice(null);
    const reader = new FileReader();
    reader.onload = () => {
      setAttachedScript({
        name: file.name,
        content: String(reader.result ?? ''),
        language: inferScriptLanguage(file.name),
      });
    };
    reader.onerror = () => setNotice({ message: 'Failed to read the uploaded script.', severity: 'error' });
    reader.readAsText(file);
  };

  const handleGenerate = async () => {
    const trimmed = prompt.trim();
    // Allow generating from just an attached script (no typed prompt needed).
    if (!trimmed && !attachedScript) return;
    setLoading(true);
    setNotice(null);
    try {
      // When a script is attached, steer the agent toward an Execute Script
      // block. The exact script body is injected client-side after generation
      // (below) so the LLM never has to reproduce it verbatim.
      let effectivePrompt = trimmed;
      if (attachedScript) {
        const scriptHint = `Run the uploaded ${attachedScript.language} script "${attachedScript.name}" in an Execute Script block.`;
        effectivePrompt = trimmed ? `${trimmed}\n\n${scriptHint}` : scriptHint;
      }

      const result = await apiClient.generateWorkflow(effectivePrompt, simulated);
      if (result.success && result.definition) {
        let definition = result.definition;
        if (attachedScript) {
          definition = injectScriptIntoDefinition(definition, attachedScript);
        }
        onGenerated(
          definition,
          result.trigger_config,
          result.name || '',
          result.description || '',
          effectivePrompt,
        );
        setExpanded(false);
      } else if (result.error_code === 'github_setup_in_progress') {
        // GitHub App installs finish asynchronously (webhook) — the user
        // just connected, so tell them to retry rather than reconnect.
        setNotice({
          message: result.error || 'GitHub is still finishing setup — this usually takes a few seconds. Try again shortly.',
          severity: 'warning',
        });
      } else if (result.error_code === 'not_a_workflow_request') {
        // The message was conversational/off-topic rather than an automation
        // description — guide instead of surfacing an integration error.
        setNotice({
          message: result.error || 'I\u2019m the Workflow Agent \u2014 I turn plain-English automation descriptions into workflows. Try something like: "When a pod is crash-looping in production, investigate the cause and post a summary to Slack."',
          severity: 'warning',
        });
      } else if (
        result.error_code === 'unconnected_integrations' &&
        result.unconnected_integrations?.length
      ) {
        // Blocked: the prompt references integrations that are not connected.
        // Rendered under the prompt so the user sees it where they typed.
        const names = result.unconnected_integrations.map(i => i.name);
        const verb = names.length === 1 ? 'is' : 'are';
        const pronoun = names.length === 1 ? 'it' : 'them';
        setNotice({
          message: `${joinNaturally(names)} ${verb} not connected. Connect ${pronoun} from the Integrations page to use ${pronoun} in a workflow.`,
          severity: 'warning',
        });
      } else if (
        result.error_code === 'unsupported_operations' &&
        result.unsupported_operations?.length
      ) {
        // Blocked: a core requested operation has no supporting workflow
        // block. Rendered under the prompt so rephrasing is one glance away.
        const quoted = result.unsupported_operations.map(o => `"${o.requested}"`);
        setNotice({
          message: `This workflow couldn't be generated: Kestrel doesn't support ${joinNaturally(quoted)} yet. Try rephrasing with supported operations, or browse the block catalog in the left sidebar to see what's available.`,
          severity: 'warning',
        });
      } else {
        setNotice({
          message: result.error || 'Failed to generate workflow. Please try again.',
          severity: 'error',
        });
      }
    } catch (err: any) {
      if (err?.response?.status === 429) {
        // Public demo builder: generation is rate limited per visitor.
        setNotice({
          message: 'You\u2019ve reached the demo generation limit. Sign up for the free trial at platform.usekestrel.ai to keep building with unlimited workflow generation.',
          severity: 'warning',
        });
      } else if (
        err?.response?.status === 403 &&
        typeof err?.response?.data === 'string' &&
        err.response.data.trim() &&
        err.response.data.trim().length < 300
      ) {
        // Billing gate (e.g. "Workflows trial has expired. Subscribe to
        // continue using workflows.") — surface the server's reason instead
        // of the generic failure text. The length guard keeps proxy HTML
        // error pages from leaking into the notice.
        setNotice({ message: err.response.data.trim(), severity: 'error' });
      } else {
        setNotice({ message: 'Failed to generate workflow. Please try again.', severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="nl-prompt-btn relative inline-flex items-center gap-2 px-5 h-10 max-md:h-auto max-md:min-h-10 max-md:py-2.5 text-sm font-medium shadow-lg"
      >
        <span className="nl-prompt-btn-border" />
        <span className="nl-prompt-btn-bg" />
        <span className="relative z-10 flex items-center gap-2 text-primary-foreground">
          <Sparkles className="w-4 h-4" />
          Describe workflow in natural language...
        </span>
      </button>
    );
  }

  return (
    <div className="w-[560px] max-md:w-[calc(100vw-24px)] border rounded-xl bg-background shadow-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/30">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Sparkles className="w-4 h-4 text-primary" />
          Workflow Agent
        </div>
        <Button variant="ghost" size="sm" onClick={() => setExpanded(false)} className="h-7 w-7 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-xs text-muted-foreground">
          Describe your workflow in plain English. The AI agent will generate a workflow you can edit.
        </p>
        <Textarea
          placeholder={"When a pod is in CrashLoopBackOff state, trigger the RCA and fix generation. If it's a YAML fix, wait for Slack approval then apply to the cluster. If it's a code fix, create a PR in GitHub and notify #incidents on Slack."}
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          rows={4}
          className="text-sm resize-none"
          disabled={loading}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleGenerate();
          }}
        />
        {attachedScript && (
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs">
            <FileCode className="w-3.5 h-3.5 text-primary shrink-0" />
            <span className="font-medium truncate flex-1" title={attachedScript.name}>{attachedScript.name}</span>
            <span className="text-muted-foreground shrink-0">{attachedScript.language}</span>
            <button
              type="button"
              onClick={() => setAttachedScript(null)}
              className="text-muted-foreground hover:text-foreground shrink-0"
              aria-label="Remove attached script"
              disabled={loading}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {notice && (
          <div
            role="alert"
            className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${
              notice.severity === 'warning'
                ? 'border-amber-500/40 bg-amber-500/5 text-foreground'
                : 'border-destructive/50 bg-destructive/5 text-destructive'
            }`}
          >
            <span className="leading-relaxed flex-1 min-w-0">{notice.message}</span>
            <button
              type="button"
              onClick={() => setNotice(null)}
              className="shrink-0 mt-0.5 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".py,.sh,.bash,.txt"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) handleScriptFile(file);
            e.target.value = '';
          }}
        />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className="h-8 w-8 p-0"
              title="Upload a script for an Execute Script block"
              aria-label="Upload a script"
            >
              <Paperclip className="w-3.5 h-3.5" />
            </Button>
            <span className="text-xs text-muted-foreground">Cmd+Enter to generate</span>
          </div>
          <Button onClick={handleGenerate} disabled={loading || (!prompt.trim() && !attachedScript)} size="sm" className="gap-2">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {loading ? 'Generating...' : 'Generate'}
          </Button>
        </div>
      </div>
    </div>
  );
}
